import { Component, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import {
  EtablissementRow,
  LogicielCaisseParametreAssociationsService,
} from 'app/services/logiciel-caisse/logiciel-caisse-parametre-associations.service';
import {
  LogicielCaisseEntrepotOption,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';

@Component({
  selector: 'app-logiciel-caisse-etablissements',
  templateUrl: './logiciel-caisse-etablissements.component.html',
  styleUrls: ['./logiciel-caisse-etablissements.component.scss'],
})
export class LogicielCaisseEtablissementsComponent implements OnInit {
  loading = false;
  saving = false;
  rows: EtablissementRow[] = [];
  filteredRows: EtablissementRow[] = [];
  searchTerm = '';
  editId: number | null = null;
  form: UntypedFormGroup;

  entrepotOptions: LogicielCaisseEntrepotOption[] = [];
  entrepotIdsByEtablissement = new Map<number, string[]>();
  entrepotLabelsByEtablissement = new Map<number, string[]>();

  associationRow: EtablissementRow | null = null;
  associationEntrepotIds: string[] = [];
  associationSaving = false;

  constructor(
    private readonly associationsService: LogicielCaisseParametreAssociationsService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly modalService: NgbModal,
    private readonly fb: UntypedFormBuilder,
    private readonly toastr: ToastrService
  ) {
    this.form = this.fb.group({
      libelle: ['', Validators.required],
      code_etablissement: ['', Validators.required],
      raison_sociale: [''],
      numero_identification_fiscale: [''],
      pays: [''],
      ville: [''],
      code_postal: [''],
      adresse: [''],
    });
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    forkJoin({
          options: this.entrepotService.getEntrepotOptions(),
          items: this.associationsService.listEtablissements(),
          links: this.associationsService.listEtablissementEntrepotLinks(),
        }).subscribe({
        next: ({ options, items, links }) => {
          this.entrepotOptions = options;
          this.rows = items;
          this.applySearch();
          const idsMap = new Map<number, string[]>();
          const labelsMap = new Map<number, string[]>();
          for (const link of links) {
            const eid = Number(link.etablissement_id);
            const idList = idsMap.get(eid) ?? [];
            idList.push(String(link.entrepot_id));
            idsMap.set(eid, idList);

            const label =
              link.entrepot_libelle?.trim() ||
              link.code_entrepot?.trim() ||
              `Entrepôt ${link.entrepot_id}`;
            const labelList = labelsMap.get(eid) ?? [];
            labelList.push(label);
            labelsMap.set(eid, labelList);
          }
          this.entrepotIdsByEtablissement = idsMap;
          this.entrepotLabelsByEtablissement = labelsMap;
          this.loading = false;
          this.refreshFeatherIcons();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.loading = false;
        },
      });
  }

  private loadEntrepotLinks(): void {
    this.associationsService.listEtablissementEntrepotLinks().subscribe({
      next: (links) => {
        const idsMap = new Map<number, string[]>();
        const labelsMap = new Map<number, string[]>();
        for (const link of links) {
          const eid = Number(link.etablissement_id);
          const idList = idsMap.get(eid) ?? [];
          idList.push(String(link.entrepot_id));
          idsMap.set(eid, idList);

          const label =
            link.entrepot_libelle?.trim() ||
            link.code_entrepot?.trim() ||
            `Entrepôt ${link.entrepot_id}`;
          const labelList = labelsMap.get(eid) ?? [];
          labelList.push(label);
          labelsMap.set(eid, labelList);
        }
        this.entrepotIdsByEtablissement = idsMap;
        this.entrepotLabelsByEtablissement = labelsMap;
        this.loading = false;
        this.refreshFeatherIcons();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
        this.loading = false;
      },
    });
  }

  entrepotsSummary(row: EtablissementRow): string {
    const labels = this.entrepotLabelsByEtablissement.get(row.id);
    if (!labels?.length) {
      return '—';
    }
    return labels.join(', ');
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredRows = [...this.rows];
    } else {
      this.filteredRows = this.rows.filter((row) => {
        const blob = `${row.libelle} ${row.code_etablissement} ${row.raison_sociale || ''} ${row.numero_identification_fiscale || ''} ${row.ville || ''} ${row.adresse || ''}`.toLowerCase();
        return blob.includes(kw);
      });
    }
    this.refreshFeatherIcons();
  }

  openCreate(modal: TemplateRef<unknown>): void {
    this.editId = null;
    this.form.reset();
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openEdit(row: EtablissementRow, modal: TemplateRef<unknown>): void {
    this.editId = row.id;
    this.form.patchValue({
      libelle: row.libelle,
      code_etablissement: row.code_etablissement,
      raison_sociale: row.raison_sociale || '',
      numero_identification_fiscale: row.numero_identification_fiscale || '',
      pays: row.pays || '',
      ville: row.ville || '',
      code_postal: row.code_postal || '',
      adresse: row.adresse || '',
    });
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openAssociation(row: EtablissementRow, modal: TemplateRef<unknown>): void {
    this.associationRow = row;
    this.associationEntrepotIds = [...(this.entrepotIdsByEtablissement.get(row.id) ?? [])];
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  save(modalRef: { close: () => void }): void {
    if (this.form.invalid) {
      return;
    }
    const v = this.form.value;
    const body = {
      libelle: String(v.libelle || '').trim(),
      code_etablissement: String(v.code_etablissement || '').trim(),
      raison_sociale: String(v.raison_sociale || '').trim() || null,
      numero_identification_fiscale: String(v.numero_identification_fiscale || '').trim() || null,
      pays: String(v.pays || '').trim() || null,
      ville: String(v.ville || '').trim() || null,
      code_postal: String(v.code_postal || '').trim() || null,
      adresse: String(v.adresse || '').trim() || null,
    };
    this.saving = true;
    const req = this.editId
      ? this.associationsService.updateEtablissement(this.editId, body)
      : this.associationsService.createEtablissement(body);
    req.subscribe({
      next: () => {
        this.saving = false;
        modalRef.close();
        this.toastr.success('Établissement enregistré.');
        this.load();
      },
      error: (err: Error) => {
        this.saving = false;
        this.toastr.error(err.message);
      },
    });
  }

  deleteRow(row: EtablissementRow): void {
    if (!confirm(`Supprimer l'établissement « ${row.libelle} » ?`)) {
      return;
    }
    this.associationsService.deleteEtablissement(row.id).subscribe({
      next: () => {
        this.toastr.success('Établissement supprimé.');
        this.load();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
      },
    });
  }

  saveAssociation(modalRef: { close: () => void }): void {
    if (!this.associationRow) {
      return;
    }
    this.associationSaving = true;
    const ids = this.associationEntrepotIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    this.associationsService.setEtablissementEntrepots(this.associationRow.id, ids).subscribe({
      next: () => {
        this.associationSaving = false;
        modalRef.close();
        this.associationRow = null;
        this.toastr.success('Association enregistrée.', 'Établissement');
        this.loadEntrepotLinks();
      },
      error: (err: Error) => {
        this.associationSaving = false;
        this.toastr.error(err.message);
      },
    });
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      if (typeof (window as unknown as { feather?: { replace: () => void } }).feather?.replace === 'function') {
        (window as unknown as { feather: { replace: () => void } }).feather.replace();
      }
    }, 0);
  }
}
